// การเรียกข้อมูลของหน้า Setting
//
// แคตตาล็อกสินค้ามาจาก Wazzup (~700 รายการ) — โหลดครั้งเดียวแล้วค้นฝั่ง client
// เหมือนของเดิม จึงตั้ง staleTime ไว้ยาว
import type { Brand, Campaign, CampaignLink, ProductGroup, WazzupProduct } from "@contracts/api";
import { inboxAuthHeaders } from "@features/inbox/api/inbox-api";
import type { BiddableAccount } from "@features/overview/api/upstream-types";
import { api } from "@shared/api/client";

export const settingsKeys = {
  products: ["settings", "products"] as const,
  groups: ["settings", "groups"] as const,
  inboxPages: ["settings", "inboxPages"] as const,
  kolCampaigns: ["settings", "kolCampaigns"] as const,
  kolCampaign: (key: string) => ["settings", "kolCampaign", key] as const,
  /** แยกตาม platform — Facebook กับ TikTok เป็นคนละชุดบัญชีกันคนละ dataset */
  biddableScope: (connector: AdPlatform) => ["settings", "biddableScope", connector] as const,
  brandMembers: (brandId: string) => ["settings", "brandMembers", brandId] as const,
  /** กลับด้านของ brandMembers — แบรนด์ที่คนคนหนึ่งเข้าถึงได้ */
  employeeBrands: (empCode: string) => ["settings", "employeeBrands", empCode] as const,
  /** สิทธิ์ของทุกคนในก้อนเดียว — หน้า Staff ต้องรู้ของทั้งรายชื่อ ไม่ใช่ทีละคน */
  allEmployeeBrands: ["settings", "allEmployeeBrands"] as const,
  biddableProjects: ["settings", "biddableProjects"] as const,
};

/* ── ใครเข้าถึงแบรนด์นี้ได้บ้าง ── */
//
// อ่านได้ถ้าอยู่ในรายชื่อของแบรนด์นั้น (หน้า Team) · แก้ได้ถ้าถือสิทธิ์ manage หรือเป็น Admin

/** viewer = เห็นแบรนด์นี้ · manage = ดูแลรายชื่อของแบรนด์นี้ได้ด้วย */
export type MemberRole = "viewer" | "manage";

export type BrandMember = {
  brandId: string;
  empCode: string;
  empName: string | null;
  role: MemberRole;
  addedBy: string | null;
  createdAt: string;
};

export async function fetchBrandMembers(brandId: string): Promise<BrandMember[]> {
  const { data } = await api.get<BrandMember[]>(`/brands/${encodeURIComponent(brandId)}/members`);
  return Array.isArray(data) ? data : [];
}

/**
 * ทุกคน → id ของแบรนด์ที่เข้าถึงได้ — ใช้แสดงโลโก้ต่อท้ายชื่อในรายชื่อพนักงาน
 *
 * คนที่ไม่มีสิทธิ์เลยจะไม่มี key ของตัวเองในผลลัพธ์ (ไม่ใช่ key ที่ชี้ไปอาเรย์ว่าง)
 */
export async function fetchBrandIdsByEmp(): Promise<Record<string, string[]>> {
  const { data } = await api.get<Record<string, string[]>>("/members");
  return data && typeof data === "object" ? data : {};
}

/** id ของแบรนด์ที่คนนี้เข้าถึงได้ — ใช้ติ๊กในแผงสิทธิ์ของหน้า Staff */
export async function fetchEmployeeBrandIds(empCode: string): Promise<string[]> {
  const { data } = await api.get<string[]>(`/members/${encodeURIComponent(empCode)}/brands`);
  return Array.isArray(data) ? data : [];
}

export async function addBrandMember(
  brandId: string,
  body: { empCode: string; empName?: string; role?: MemberRole },
): Promise<BrandMember> {
  const { data } = await api.post<BrandMember>(
    `/brands/${encodeURIComponent(brandId)}/members`,
    body,
  );
  return data;
}

export async function setBrandMemberRole(
  brandId: string,
  empCode: string,
  role: MemberRole,
): Promise<BrandMember> {
  const { data } = await api.patch<BrandMember>(
    `/brands/${encodeURIComponent(brandId)}/members/${encodeURIComponent(empCode)}`,
    { role },
  );
  return data;
}

export async function removeBrandMember(brandId: string, empCode: string): Promise<void> {
  await api.delete(`/brands/${encodeURIComponent(brandId)}/members/${encodeURIComponent(empCode)}`);
}

/** platform ของโฆษณาที่ Biddable รองรับ — ตรงกับ connector ของ /api/scope ฝั่งเขา */
export type AdPlatform = "facebook" | "tiktok";

export async function fetchProducts(): Promise<WazzupProduct[]> {
  const { data } = await api.get<WazzupProduct[]>("/wazzup/products");
  return Array.isArray(data) ? data : [];
}

export async function fetchGroups(): Promise<ProductGroup[]> {
  const { data } = await api.get<ProductGroup[]>("/groups");
  return Array.isArray(data) ? data : [];
}

export async function createGroup(body: {
  name: string;
  color: string;
  members: string[];
}): Promise<ProductGroup> {
  const { data } = await api.post<ProductGroup>("/groups", body);
  return data;
}

export async function updateGroup(
  id: string,
  body: { name?: string; members?: string[]; logoUrl?: string | null },
): Promise<ProductGroup> {
  const { data } = await api.put<ProductGroup>(`/groups/${encodeURIComponent(id)}`, body);
  return data;
}

export async function deleteGroup(id: string): Promise<void> {
  await api.delete(`/groups/${encodeURIComponent(id)}`);
}

/* ── brands = สิ่งที่ enable เข้า My Job (enable = insert, disable = delete) ── */

export async function enableBrand(body: {
  id: string;
  kind?: "product" | "group";
  name?: string;
  tagline?: string;
  color?: string;
  logoUrl?: string | null;
}): Promise<Brand> {
  const { data } = await api.post<Brand>("/brands", body);
  return data;
}

export async function disableBrand(id: string): Promise<void> {
  await api.delete(`/brands/${encodeURIComponent(id)}`);
}

export async function updateBrand(id: string, body: Record<string, unknown>): Promise<Brand> {
  const { data } = await api.put<Brand>(`/brands/${encodeURIComponent(id)}`, body);
  return data;
}

/* ── เพจของ Inbox Center ── */

export type PickablePage = { id: string; name: string; pictureUrl: string | null };

/**
 * รายการเพจสำหรับ dropdown ผูกเพจ
 * ยังไม่ได้ login ที่ไหนเลย → โยน error ให้ UI สลับไปโหมดกรอก Page ID เอง
 */
export async function fetchPickablePages(): Promise<PickablePage[]> {
  const headers = inboxAuthHeaders();
  if (!headers) throw new Error("ยังไม่ได้เข้าสู่ระบบ Wazzup");
  const { data } = await api.get<unknown>("/inbox/pages", { headers });
  const raw = Array.isArray(data) ? data : ((data as { pages?: unknown[] })?.pages ?? []);
  return (
    (raw as Record<string, unknown>[])
      .map((x) => ({
        id: String(x.id ?? x.pageId ?? x.page_id ?? ""),
        name: String(x.name ?? x.pageName ?? x.page_name ?? ""),
        pictureUrl: (x.pictureUrl ?? x.picture_url ?? null) as string | null,
      }))
      .filter((x) => x.id)
      // เรียงตามตัวอักษร รองรับไทย (locale 'th') แบบเดียวกับฝั่ง Inbox Center
      .sort((a, b) => a.name.localeCompare(b.name, "th"))
  );
}

/* ── รายชื่อแคมเปญฝั่ง KOL Report ── */

export type KolCampaignOption = {
  key: string;
  name: string;
  rosterCount: number | null;
  refreshedAt: string | null;
};

/**
 * ชื่อแคมเปญ KOL ตัวเดียว
 *
 * ใช้ตอนที่ดึงรายการทั้งหมดไม่ได้ (KOL ไม่รับ token ใบนี้) — ฝั่งเขาเปิดให้อ่านรายตัว
 * โดยไม่ต้อง login จึงยังแปลรหัสที่ผูกไว้เป็นชื่อให้คนอ่านเข้าใจได้เสมอ
 */
export async function fetchKolCampaign(key: string): Promise<KolCampaignOption> {
  const { data } = await api.get<KolCampaignOption>(`/kol/campaigns/${encodeURIComponent(key)}`, {
    headers: inboxAuthHeaders() ?? undefined,
  });
  return data;
}

export async function fetchKolCampaigns(): Promise<KolCampaignOption[]> {
  const headers = inboxAuthHeaders();
  if (!headers) throw new Error("ยังไม่ได้เข้าสู่ระบบ Wazzup");
  const { data } = await api.get<KolCampaignOption[]>("/kol/campaigns", { headers });
  return Array.isArray(data) ? [...data].sort((a, b) => a.name.localeCompare(b.name, "th")) : [];
}

/**
 * Ad Account + แคมเปญฝั่ง Biddable
 * แยกสาเหตุที่โหลดไม่ได้ให้ชัด — เดิมล้มเหลวเงียบ ๆ แล้วตกไปช่องกรอกเอง
 * ทำให้ไม่รู้ว่าติดสิทธิ์หรือปลายทางช้า
 */
export async function fetchBiddableAccounts(connector: AdPlatform): Promise<BiddableAccount[]> {
  const headers = inboxAuthHeaders();
  if (!headers) throw new Error("ยังไม่ได้เข้าสู่ระบบ Wazzup");
  const { data, status } = await api.get<{ accounts?: BiddableAccount[]; error?: string }>(
    "/biddable/scope",
    { headers, params: { connector }, timeout: 180_000, validateStatus: () => true },
  );
  if (status === 403)
    throw new Error("บัญชีนี้ไม่มีสิทธิ์ดูข้อมูล Biddable (ต้องมี role Windsor Admin)");
  if (status >= 400) throw new Error(data?.error || `ปลายทางตอบ HTTP ${status}`);
  if (!Array.isArray(data?.accounts) || !data.accounts.length)
    throw new Error(
      `Biddable ยังไม่มีข้อมูลบัญชี ${connector === "tiktok" ? "TikTok" : "Facebook"} ในช่วง 30 วันล่าสุด`,
    );
  return data.accounts;
}

/* ── project ฝั่ง Biddable (หน่วยที่หน้า Campaign Report ของเขาจัดกลุ่ม) ── */

export type BiddableProject = {
  /** รหัสงานในชื่อแคมเปญ เช่น P2026-124 — เป็นตัวระบุตัวตน ไม่ใช่ชื่อ */
  code: string;
  name: string;
  platforms: string[];
  accounts: string[];
  campaigns: number;
  spend: number;
  lastDate: string;
};

export async function fetchBiddableProjects(): Promise<BiddableProject[]> {
  const headers = inboxAuthHeaders();
  if (!headers) throw new Error("ยังไม่ได้เข้าสู่ระบบ");
  const { data, status } = await api.get<{ projects?: BiddableProject[]; error?: string }>(
    "/biddable/projects",
    { headers, timeout: 120_000, validateStatus: () => true },
  );
  if (status === 403)
    throw new Error("บัญชีนี้ไม่มีสิทธิ์ดูข้อมูล Biddable (ต้องมี role Windsor Admin)");
  if (status >= 400) throw new Error(data?.error || `ปลายทางตอบ HTTP ${status}`);
  if (!Array.isArray(data?.projects) || !data.projects.length)
    throw new Error("Biddable ยังไม่มีงานที่มีรหัส project ในชื่อแคมเปญ");
  return data.projects;
}

/* ── แคมเปญของเราเอง + สิ่งที่ผูกไว้ ── */

export async function createCampaign(body: {
  productCode: string;
  name: string;
}): Promise<Campaign> {
  const { data } = await api.post<Campaign>("/campaigns", body);
  return data;
}

/* ── เอกสาร Finance ของทุกแบรนด์ (Setting → Finance Document) ── */

export type FinanceDoc = {
  id: string;
  name: string;
  size: number;
  category: string | null;
  createdAt: string;
  brandId: string | null;
  brandName: string | null;
  campaignId: string | null;
  campaignName: string | null;
};

export const financeDocKeys = { all: ["financeDocs"] as const };

export async function fetchFinanceDocs(): Promise<FinanceDoc[]> {
  const { data } = await api.get<{ documents?: FinanceDoc[] }>("/documents/finance");
  return Array.isArray(data?.documents) ? data.documents : [];
}

/* ── ทะเบียนงานจาก Sheet Project Code ── */

export type ProjectCode = {
  code: string;
  planner: string[];
  hybrid: string;
  kolBuyer: string[];
  biddable: string[];
  createBy: string;
  year: string;
  no: string;
  brand: string;
  name: string;
  status: string;
  /** ชื่อที่จะใช้ตั้งเป็นแคมเปญ */
  campaignName: string;
  /** ไฟล์ Master Plan ที่ผูกกับรหัสงานนี้ — ว่างได้ (ยังไม่มีใครอัปโหลด) */
  masterPlans?: { id: string; name: string }[];
  /**
   * มีแคมเปญของงานนี้ที่ติ๊กไว้ว่าเป็นคอนเทนต์รายเดือน
   * = ช่องว่างในคอลัมน์ Master Plan ของงานนี้เป็นเรื่องปกติ ไม่ใช่ของที่ขาด
   */
  monthlyContent?: boolean;
};

export async function fetchProjectCodes(
  brandId: string,
): Promise<{ terms: string[]; projects: ProjectCode[] }> {
  const { data } = await api.get<{ terms?: string[]; projects?: ProjectCode[] }>(
    `/project-codes/${encodeURIComponent(brandId)}`,
    // อ่านไฟล์ทั้งใบจาก Google ครั้งแรกใช้เวลาได้หลายวินาที
    { timeout: 60_000 },
  );
  return {
    terms: Array.isArray(data?.terms) ? data.terms : [],
    projects: Array.isArray(data?.projects) ? data.projects : [],
  };
}

export const projectCodeKeys = {
  byBrand: (brandId: string) => ["projectCodes", brandId] as const,
  all: ["projectCodes", "all"] as const,
};

/** ทะเบียนทั้งใบสำหรับหน้า Setting → Project Code · refresh = ข้ามแคชฝั่ง server */
export async function fetchProjectRegister(
  refresh = false,
): Promise<{ fetchedAt: string; sheetUrl: string; rows: ProjectCode[] }> {
  const { data } = await api.get<{
    fetchedAt?: string;
    sheetUrl?: string;
    rows?: ProjectCode[];
  }>(`/project-codes${refresh ? "?refresh=1" : ""}`, { timeout: 60_000 });
  return {
    fetchedAt: data?.fetchedAt ?? "",
    sheetUrl: data?.sheetUrl ?? "",
    rows: Array.isArray(data?.rows) ? data.rows : [],
  };
}

/** เปลี่ยนชื่อแคมเปญ — server รับ name เดี่ยว ๆ ได้ ไม่ต้องส่งทั้งก้อน */
export async function renameCampaign(id: string, name: string): Promise<Campaign> {
  const { data } = await api.put<Campaign>(`/campaigns/${encodeURIComponent(id)}`, { name });
  return data;
}

/** ติ๊ก/ปลดติ๊กว่าแคมเปญนี้เป็นคอนเทนต์รายเดือน (ไม่ต้องมี Master Plan) */
export async function setCampaignMonthly(id: string, on: boolean): Promise<Campaign> {
  const { data } = await api.put<Campaign>(`/campaigns/${encodeURIComponent(id)}`, {
    monthlyContent: on,
  });
  return data;
}

export async function deleteCampaign(id: string): Promise<void> {
  await api.delete(`/campaigns/${encodeURIComponent(id)}`);
}

export async function addCampaignLink(
  campaignId: string,
  body: { kind: string; refId: string; refName?: string | null; meta?: unknown },
): Promise<CampaignLink> {
  const { data } = await api.post<CampaignLink>(
    `/campaigns/${encodeURIComponent(campaignId)}/links`,
    body,
  );
  return data;
}

export async function deleteCampaignLink(id: string): Promise<void> {
  await api.delete(`/campaign-links/${encodeURIComponent(id)}`);
}
